import { useCallback, useMemo } from 'react'
import { Form as ReactFinalForm, FormProps } from 'react-final-form'
import { IErrors } from 'components/types/app'
import { AnyObject, Mutator } from 'final-form'
import { set } from 'lodash'

import { createScrollToErrorDecorator } from './decorators'
import { typedArrayMutatorsCreator } from './helpers'
import { CustomFormProps } from './types'

export function Form<FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>>({
  mutators,
  decorators,
  onSubmit,
  ...props
}: CustomFormProps<FormValues, InitialFormValues>) {
  const mutatorsPrepared = useMemo<Record<string, Mutator<FormValues, InitialFormValues>>>(
    () => ({
      ...mutators,
      ...typedArrayMutatorsCreator<FormValues, InitialFormValues>()
    }),
    [mutators]
  )

  const scrollToErrorDecorator = useMemo(
    () => createScrollToErrorDecorator<FormValues, InitialFormValues>(props.name),
    [props.name]
  )
  const decoratorsPrepared = useMemo(
    () => [scrollToErrorDecorator, ...(decorators || [])],
    [decorators, scrollToErrorDecorator]
  )

  const handleSubmit = useCallback<FormProps<FormValues, InitialFormValues>['onSubmit']>(
    (values, form) => {
      const result = onSubmit(values, form)
      if (result) {
        return (result instanceof Promise ? result : Promise.resolve(result)).then((errors?: IErrors | void) => {
          if (errors) {
            return Object.entries(errors).reduce((accErrors, [path, { title }]) => {
              set(accErrors, path, title)

              return accErrors
            }, {} as AnyObject)
          }
        })
      }
    },
    [onSubmit]
  )

  return (
    <ReactFinalForm decorators={decoratorsPrepared} mutators={mutatorsPrepared} onSubmit={handleSubmit} {...props} />
  )
}
